/**
 * Structure of the telc Jezyk polski B1-B2 Szkola exam.
 *
 * Every number here comes from the official telc document
 * "Egzamin probny 1, Jezyk polski B1-B2 Szkola" ((c) telc GmbH, 2013):
 *  - p. 5   exam structure, task counts and timings
 *  - p. 37  points and partial results (test modules, Writing)
 *  - p. 38  oral exam points
 *  - p. 39  conditions for a B1 / B2 certificate
 *
 * This is the only place these rules are written down. Views and scoring read
 * from here — never copy the numbers into a component.
 *
 * The shapes these numbers fill in live in `exam-family.ts`, because the state
 * exam fills in the same shapes with different numbers. They are re-exported
 * below: telc was here first and most of the app still speaks it directly, so
 * an import of `ModuleId` or `Threshold` from this module keeps working.
 */
import type {
  ExamVariant,
  Level,
  ModuleId,
  ModuleSpec,
  TestModuleId,
  Threshold,
} from "./exam-family.ts";
import { TEST_MODULES } from "./exam-family.ts";

export type { ExamVariant, Level, ModuleId, ModuleSpec, TestModuleId, Threshold };
export { TEST_MODULES };

/**
 * telc publishes a time and a point count for every module, so its own module
 * specs never carry the `null` the shared shape allows.
 */
export interface TelcModuleSpec extends ModuleSpec {
  minutes: number;
  items: number;
  /** Item numbers on the S30 answer sheet. */
  range: [number, number];
}

/** Written exam blocks in the order they are actually sat. */
export const MODULES: TelcModuleSpec[] = [
  { id: "listening", name: "Rozumienie ze słuchu", minutes: 25, items: 23, range: [1, 23] },
  { id: "reading", name: "Rozumienie tekstu pisanego", minutes: 40, items: 18, range: [24, 41] },
  { id: "elements", name: "Elementy języka", minutes: 20, items: 18, range: [42, 59] },
  { id: "writing", name: "Pisanie", minutes: 30, items: 0, range: [0, 0] },
];

export const MODULE_BY_ID = Object.fromEntries(MODULES.map((m) => [m.id, m])) as Record<
  ModuleId,
  TelcModuleSpec
>;

/** Listening + Reading + Language elements = 59 items, one point each. */
export const TEST_MAX = 59;
/** Writing: 4 criteria worth 5 points each. */
export const WRITING_MAX = 20;
/** Oral exam: 100 points (p. 38). */
export const SPEAKING_MAX = 100;

/** Total length of the written exam, in minutes. */
export const WRITTEN_MINUTES = MODULES.reduce((s, m) => s + m.minutes, 0); // 115

/** p. 37 — test modules (0-59). */
export const TEST_THRESHOLDS: Threshold[] = [
  { level: "B2", min: 43, max: TEST_MAX },
  { level: "B1", min: 27, max: 42 },
  { level: "below", min: 0, max: 26 },
];

/** p. 37 — Writing (0-20). */
export const WRITING_THRESHOLDS: Threshold[] = [
  { level: "B2", min: 15, max: WRITING_MAX },
  { level: "B1", min: 7, max: 14 },
  { level: "below", min: 0, max: 6 },
];

/** p. 38 — oral exam (0-100). B1 starts at 35, B2 at 75. */
export const SPEAKING_THRESHOLDS: Threshold[] = [
  { level: "B2", min: 75, max: SPEAKING_MAX },
  { level: "B1", min: 35, max: 74.5 },
  { level: "below", min: 0, max: 34.5 },
];

/**
 * The one telc variant this app simulates.
 *
 * One paper, graded into B1 or B2 — which is exactly what the state exam does
 * not do, and the reason a set has to say which variant it belongs to.
 */
export const TELC_VARIANT: ExamVariant = {
  id: "telc.b1b2.school",
  family: "telc",
  levels: ["B1", "B2"],
  group: "adults",
  modules: MODULES,
  writtenMinutes: WRITTEN_MINUTES,
  oralMinutes: 16,
  pass: { kind: "telc" },
  source: "telc GmbH, Egzamin próbny 1, Język polski B1-B2 Szkoła (2013), pp. 5, 37-39",
};

/**
 * Assessment criteria for the Writing module (pp. 33-34, points table on p. 37).
 * Each criterion scores 5 / 4 / 3 / 2 / 1 / 0.
 */
export const WRITING_CRITERIA = [
  { id: "tresc", roman: "I" },
  { id: "kompozycja", roman: "II" },
  { id: "poprawnosc", roman: "III" },
  { id: "slownictwo", roman: "IV" },
] as const;

export type WritingCriterionId = (typeof WRITING_CRITERIA)[number]["id"];

/**
 * The band scale shared by all four writing criteria (p. 37).
 * `labelKey` names an entry under `criteria.` in the interface catalogues —
 * the band names have to be readable to whoever is scoring themselves.
 */
export const WRITING_BANDS = [
  { points: 5, labelKey: "bandB2High" },
  { points: 4, labelKey: "bandB2Low" },
  { points: 3, labelKey: "bandB1High" },
  { points: 2, labelKey: "bandB1Low" },
  { points: 1, labelKey: "bandA2" },
  { points: 0, labelKey: "bandZero" },
] as const;

/**
 * Oral exam point distribution (p. 38).
 * `max` is what the top B2 band awards; the scale steps down 100/80/60/40/20/0 %.
 */
export const SPEAKING_CRITERIA = [
  { id: "cz1a", max: 5 },
  { id: "cz1b", max: 5 },
  { id: "cz2a", max: 10 },
  { id: "cz2b", max: 10 },
  { id: "cz3", max: 20 },
  { id: "wymowa", max: 10 },
  { id: "plynnosc", max: 10 },
  { id: "poprawnosc", max: 15 },
  { id: "slownictwo", max: 15 },
] as const;

export type SpeakingCriterionId = (typeof SPEAKING_CRITERIA)[number]["id"];

/** The six telc bands, expressed as a fraction of a criterion's maximum (p. 38). */
export const SPEAKING_BANDS = [
  { ratio: 1, labelKey: "bandB2High" },
  { ratio: 0.8, labelKey: "bandB2Low" },
  { ratio: 0.6, labelKey: "bandB1High" },
  { ratio: 0.4, labelKey: "bandB1Low" },
  { ratio: 0.2, labelKey: "bandA2" },
  { ratio: 0, labelKey: "bandZero" },
] as const;

/** Reading time given before a recording starts (pp. 8-9). */
export const LISTENING_READ_SECONDS = {
  /** Part 3 — "Masz na to minute." (one minute) */
  interview: 60,
  /** Part 4 — "Masz pol minuty na przeczytanie zdan a-f." (thirty seconds) */
  opinions: 30,
} as const;

/** Gap between recordings within one part, in seconds. */
export const LISTENING_GAP_SECONDS = 3;

/**
 * The parts of telc's written exam, in the order they are sat, and how many
 * points each is worth (one per item).
 *
 * The kinds are the task kinds of `schema.ts`; `tests/exam-tasks.test.ts` counts
 * them in every telc set. telc's paper is the same shape every time, so a page
 * can describe it without reading any set.
 */
export const TELC_TASKS = {
  listening: [
    { kind: "messages", items: 4 },
    { kind: "dialogs", items: 10 },
    { kind: "interview", items: 6 },
    { kind: "opinions", items: 3 },
  ],
  reading: [
    { kind: "emails", items: 4 },
    { kind: "forum", items: 5 },
    { kind: "texts", items: 6 },
    { kind: "trueFalse", items: 3 },
  ],
  elements: [
    { kind: "gapBank", items: 8 },
    { kind: "gapMc", items: 10 },
  ],
} as const;
